import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
import {
  DateInputProps,
  NumberInputProps,
  SearchInputProps,
  SelectInputProps,
  NumberInputWithSuggestionsProps,
  TextInputProps,
  ToggleProps,
} from "models/propTypes/wintFormPropsTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { RecurringConfigurationFieldsProps } from "models/propTypes/invoicePropTypes";
//#endregion
//#region components
import {
  HorizontalFormField,
  NumberInput,
  SearchInput,
  TextInput,
  GroupSection,
  SelectInput,
  DateInput,
  Toggle,
  NumberInputWithSuggestions,
} from "components/common/wintForms";
import { SendMailFormFields } from "components/invoice";
import { useCompanyService, useRecurringInvoiceService } from "serviceHooks";
//#endregion
import { DateHelper, EnumHelper, LocalizationHelperInstance } from "utils/helpers";

const RecurringConfigurationStepFields: React.FC<RecurringConfigurationFieldsProps> = (props) => {
  const companyService = useCompanyService();
  const recurringInvoiceService = useRecurringInvoiceService();
  const { connect, values, touched, invoice } = props;

  const hideDeliveryDate = useSelector((store: AppState) => store.auth.selectedCompany.hideInvoiceDeliveryDate);

  const isRecurringInvoiceDraft =
    invoice?.id === undefined || invoice?.status === WintSuperkollAPIModel.RecurringInvoiceState.Created;

  const recurringFrequencyProps = connect("recurringSettings.recurringInvoiceFrequency");

  const onRecurringFrequencyChange = (e: React.ChangeEvent<any>) => {
    recurringFrequencyProps.onChange(e);
    updateRecurringPeriodisation(e.target.value);
  };

  const updateRecurringPeriodisation = (frequency: WintSuperkollAPIModel.RecurringInvoiceFrequency) => {
    const newRecurringPeriodisationLength = recurringInvoiceService.getFrequencyMonths(frequency);

    if (
      (!values.isPeriodisationAdvancedConfiguration &&
        (!touched?.products ||
          !(
            Array.isArray(touched.products) &&
            (touched.products[0][FormFieldConstants.Invoice.Product.RecurringPeriodisation]?.[
              FormFieldConstants.Invoice.RecurringPeriodisation.From
            ] ||
              touched.products[0][FormFieldConstants.Invoice.Product.RecurringPeriodisation]?.[
                FormFieldConstants.Invoice.RecurringPeriodisation.Duration
              ])
          ))) ||
      (values.isPeriodisationAdvancedConfiguration &&
        (!touched?.products ||
          !(
            Array.isArray(touched.products) &&
            (touched.products.some(
              (p) =>
                p[FormFieldConstants.Invoice.Product.RecurringPeriodisation]?.[
                  FormFieldConstants.Invoice.RecurringPeriodisation.From
                ]
            ) ||
              touched.products.some(
                (p) =>
                  p[FormFieldConstants.Invoice.Product.RecurringPeriodisation]?.[
                    FormFieldConstants.Invoice.RecurringPeriodisation.Duration
                  ]
              ))
          )))
    ) {
      const updatedProducts = [...(values.products ?? [])];

      updatedProducts.forEach((p) => {
        if (p.recurringPeriodisation) p.recurringPeriodisation.duration = newRecurringPeriodisationLength;
      });
      connect(FormFieldConstants.Invoice.CreateBase.Products).setValue(updatedProducts);
    }

    return undefined;
  };

  const sendTypeProps = connect("recurringSettings.recurringInvoiceAutomaticSendType");

  return (
    <>
      <GroupSection
        header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Generally)}
        icon={IconDefinitionConstants.WInfo}
      >
        <HorizontalFormField<TextInputProps>
          Component={TextInput}
          {...connect("recurringSettings.recurringDescription")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Description)}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.RecurringDescriptionHelpText
          )}
        />
        <HorizontalFormField<SearchInputProps>
          Component={SearchInput}
          {...connect("recurringSettings.recurringOwner")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Owner)}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.RecurringOwnerDescription
          )}
          items={(searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false)}
        />
        <HorizontalFormField<SelectInputProps>
          Component={SelectInput}
          {...connect("recurringSettings.recurringInvoiceSendType")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ManageRecurring)}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.ManageRecurringDescription
          )}
          items={EnumHelper.getNumericItemValues(WintSuperkollAPIModel.RecurringInvoiceSendType).map((key) => {
            return {
              value: Number(key),
              label: LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringSendType_${key}`),
            };
          })}
        />
        {values.recurringSettings.recurringInvoiceSendType === WintSuperkollAPIModel.RecurringInvoiceSendType.Auto ? (
          <HorizontalFormField<SelectInputProps>
            Component={SelectInput}
            {...sendTypeProps}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HowRecurringSent)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.HowRecurringSentDescription
            )}
            items={EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceSendType).map((key) => {
              return {
                value: Number(key),
                label: LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringAutomaticSendType_${key}`),
              };
            })}
          />
        ) : (
          <></>
        )}
      </GroupSection>
      {values.recurringSettings.recurringInvoiceSendType === WintSuperkollAPIModel.RecurringInvoiceSendType.Auto &&
      values.recurringSettings.recurringInvoiceAutomaticSendType ===
        WintSuperkollAPIModel.InvoiceSendType.SendByEmail ? (
        <GroupSection
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Email)}
          icon={IconDefinitionConstants.WPaperPlane}
        >
          <SendMailFormFields
            connect={connect}
            invoiceId={invoice?.id}
            mailOptions={values.mailOptions}
            isNotSendForm
            parentFieldName="recurringSettings.recurringSendMail"
          />
        </GroupSection>
      ) : (
        <></>
      )}
      <GroupSection
        header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Dates)}
        icon={IconDefinitionConstants.WMonthCalendar}
      >
        <HorizontalFormField<DateInputProps>
          Component={DateInput}
          {...connect("recurringSettings.recurringInvoiceStartDate")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.FirstInvoice)}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.FirstInvoiceDescription
          )}
          minDate={DateHelper.addDays(DateHelper.today(), 1)}
          disabled={!isRecurringInvoiceDraft}
          customMessage={
            isRecurringInvoiceDraft &&
            DateHelper.isBefore(values.recurringSettings.recurringInvoiceStartDate, DateHelper.today())
              ? {
                  type: StyleEnums.DisplayType.Info,
                  message: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Validations.ShouldBeFutureDate
                  ),
                }
              : undefined
          }
        />
        <HorizontalFormField<SelectInputProps>
          Component={SelectInput}
          {...recurringFrequencyProps}
          onChange={onRecurringFrequencyChange}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HowOften)}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HowOftenDescription)}
          items={EnumHelper.getNumericItemValues(WintSuperkollAPIModel.RecurringInvoiceFrequency).map((key) => {
            return {
              value: Number(key),
              label: LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringFrequency_${key}`),
            };
          })}
        />
        <HorizontalFormField<ToggleProps>
          Component={Toggle}
          {...connect("recurringSettings.recurringRepeatUntilNotice")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RepeatUntilNotice)}
        />
        {!values.recurringSettings.recurringRepeatUntilNotice ? (
          <HorizontalFormField<DateInputProps>
            Component={DateInput}
            {...connect("recurringSettings.recurringInvoiceEndDate")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.LastInvoice)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.LastInvoiceDescription
            )}
            minDate={values.recurringSettings.recurringInvoiceStartDate}
          />
        ) : (
          <></>
        )}
        {hideDeliveryDate ? (
          <HorizontalFormField<NumberInputProps>
            Component={NumberInput}
            {...connect("recurringSettings.recurringDeliveryAfterDays")}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.DeliveryDate)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.RecurringDeliveryDateDescription
            )}
            allowNegative
            suffix={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Days).toLowerCase()}
          />
        ) : (
          <></>
        )}
        <HorizontalFormField<NumberInputWithSuggestionsProps>
          Component={NumberInputWithSuggestions}
          {...connect("otherSettings.paymentTerms")}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PaymentTerms)}
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.PaymentTermsDescription
          )}
          suggestions={InvoiceConstants.PaymentTerms}
          allowNegative
          suffix={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Days).toLowerCase()}
        />
      </GroupSection>
    </>
  );
};

export default RecurringConfigurationStepFields;
